import React, { useState, useEffect } from 'react';
import { Card, Typography, Progress, Select, Button, Space, Divider, Spin, Alert } from 'antd';
import { 
  TrophyOutlined, 
  CalendarOutlined, 
  DollarOutlined,
  ReloadOutlined,
  SettingOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useDealStore from '../store/dealStore';
import useTargetStore from '../store/targetStore';
import useSettingsStore from '../store/settingsStore';
import useAuthStore from '../store/authStore';
import apiService from '../services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;

const TargetSidebar = () => {
  const navigate = useNavigate();
  const { deals, fetchDeals, stats: dealStats, fetchDealStats } = useDealStore();
  const { 
    targetHierarchy, 
    isLoading: targetLoading, 
    error: targetError,
    fetchTargetHierarchy,
    refreshAchievements 
  } = useTargetStore();
  const { formatCurrency } = useSettingsStore();
  const { user: currentUser } = useAuthStore();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [userTargetHierarchy, setUserTargetHierarchy] = useState(null);
  const [allUsersTargets, setAllUsersTargets] = useState([]);
  const [displayHierarchy, setDisplayHierarchy] = useState(null);

  // Fetch user-specific targets
  const fetchUserTargets = async (year) => {
    try {
      if (currentUser?._id) {
        console.log('Fetching targets for user:', currentUser._id, 'year:', year);
        const response = await apiService.get(`/targets/user/${currentUser._id}/hierarchy/${year}`);
        console.log('User targets response:', response.data);
        if (response.data?.success) {
          console.log('Setting user target hierarchy:', response.data.data);
          setUserTargetHierarchy(response.data.data);
        }
      } else {
        console.log('No currentUser ID available');
      }
    } catch (error) {
      console.error('Error fetching user targets:', error);
    }
  };

  // Fetch all users' targets for tenant_admin
  const fetchAllUsersTargets = async (year) => {
    try {
      if (currentUser?.role === 'tenant_admin') {
        console.log('Fetching all users targets for admin, year:', year);
        const response = await apiService.get(`/targets?year=${year}`);
        console.log('All users targets response:', response.data);
        if (response.data?.success) {
          console.log('Setting all users targets:', response.data.data);
          setAllUsersTargets(response.data.data);
        }
      } else {
        console.log('Not a tenant_admin, skipping fetchAllUsersTargets. Role:', currentUser?.role);
      }
    } catch (error) {
      console.error('Error fetching all users targets:', error);
    }
  };

  // Calculate summed targets for tenant_admin
  const calculateSummedTargets = (targets) => {
    console.log('Calculating summed targets from:', targets);
    console.log('Total targets received:', targets.length);
    const hierarchy = {
      year: selectedYear,
      annual: null,
      quarters: [],
      months: []
    };

    // For tenant_admin, sum ALL targets (both user-level and org-level)
    // Include all targets regardless of userId
    const annualTargets = targets.filter(t => t.type === 'annual');
    const quarterlyTargets = targets.filter(t => t.type === 'quarterly');
    const monthlyTargets = targets.filter(t => t.type === 'monthly');
    
    console.log('Filtered targets - annual:', annualTargets.length, 'quarterly:', quarterlyTargets.length, 'monthly:', monthlyTargets.length);
    console.log('All targets details:', targets.map(t => ({ 
      _id: t._id,
      type: t.type, 
      userId: t.userId, 
      amount: t.targetAmount, 
      period: t.period,
      year: t.year
    })));
    
    if (quarterlyTargets.length > 0) {
      console.log('Quarterly targets:', quarterlyTargets.map(t => ({ 
        period: t.period, 
        amount: t.targetAmount, 
        userId: t.userId,
        _id: t._id
      })));
      
      // Group by period to see aggregation
      const quartersByPeriod = {};
      quarterlyTargets.forEach(t => {
        if (!quartersByPeriod[t.period]) quartersByPeriod[t.period] = [];
        quartersByPeriod[t.period].push({ userId: t.userId, amount: t.targetAmount });
      });
      console.log('Quarterly targets grouped by period:', quartersByPeriod);
    }

    if (annualTargets.length > 0) {
      // Create aggregated annual target without spreading individual target properties
      const totalAnnualAmount = annualTargets.reduce((sum, t) => sum + t.targetAmount, 0);
      const totalAnnualAchieved = annualTargets.reduce((sum, t) => sum + t.achievedAmount, 0);
      
      hierarchy.annual = {
        year: selectedYear,
        type: 'annual',
        targetAmount: totalAnnualAmount,
        achievedAmount: totalAnnualAchieved,
        currency: annualTargets[0].currency || 'INR'
      };
      console.log('Annual targets count:', annualTargets.length);
      console.log('Total annual amount:', totalAnnualAmount);
      console.log('Aggregated annual target:', hierarchy.annual);
    }

    const quarterMap = {};
    quarterlyTargets.forEach(target => {
      if (!quarterMap[target.period]) {
        quarterMap[target.period] = { ...target, targetAmount: 0, achievedAmount: 0 };
      }
      quarterMap[target.period].targetAmount += target.targetAmount;
      quarterMap[target.period].achievedAmount += target.achievedAmount;
    });
    hierarchy.quarters = Object.values(quarterMap);
    
    console.log('Final aggregated quarters:', hierarchy.quarters.map(q => ({
      period: q.period,
      targetAmount: q.targetAmount,
      achievedAmount: q.achievedAmount
    })));

    const monthMap = {};
    monthlyTargets.forEach(target => {
      if (!monthMap[target.period]) {
        monthMap[target.period] = { ...target, targetAmount: 0, achievedAmount: 0 };
      }
      monthMap[target.period].targetAmount += target.targetAmount;
      monthMap[target.period].achievedAmount += target.achievedAmount;
    });
    hierarchy.months = Object.values(monthMap);

    return hierarchy;
  };

  // Fetch target data on mount and when year changes
  useEffect(() => {
    fetchUserTargets(selectedYear);
    fetchAllUsersTargets(selectedYear);
    fetchDeals();
    fetchDealStats();
  }, [selectedYear, fetchDeals, fetchDealStats, currentUser]);

  // Set display hierarchy based on user role
  useEffect(() => {
    console.log('Setting display hierarchy. Role:', currentUser?.role);
    console.log('userTargetHierarchy:', userTargetHierarchy);
    console.log('allUsersTargets length:', allUsersTargets.length);
    
    if (currentUser?.role === 'tenant_admin') {
      console.log('User is tenant_admin, using aggregated targets');
      if (allUsersTargets.length > 0) {
        const summed = calculateSummedTargets(allUsersTargets);
        console.log('Calculated summed targets:', summed);
        setDisplayHierarchy(summed);
      } else {
        console.log('No all users targets available');
        setDisplayHierarchy({ year: selectedYear, annual: null, quarters: [], months: [] });
      }
    } else {
      console.log('User is regular tenant, using their own targets');
      if (userTargetHierarchy) {
        setDisplayHierarchy(userTargetHierarchy);
      } else {
        setDisplayHierarchy({ year: selectedYear, annual: null, quarters: [], months: [] });
      }
    }
  }, [userTargetHierarchy, allUsersTargets, currentUser?.role, selectedYear]);

  // Auto-refresh target data every 30 seconds to catch updates from deal changes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUserTargets(selectedYear);
      fetchAllUsersTargets(selectedYear);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [selectedYear]);

  // Refresh when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTargetHierarchy(selectedYear);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedYear, fetchTargetHierarchy]);

  // Get current quarter based on fiscal year (April-March)
  const getCurrentQuarter = () => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    if (currentMonth >= 4 && currentMonth <= 6) return 'Q1';
    if (currentMonth >= 7 && currentMonth <= 9) return 'Q2';
    if (currentMonth >= 10 && currentMonth <= 12) return 'Q3';
    return 'Q4'; // Jan-Mar
  };
  
  const currentQuarter = getCurrentQuarter();
  
  // Get current quarter data
  const getCurrentQuarterData = () => {
    if (!displayHierarchy || !displayHierarchy.quarters) return null;
    return displayHierarchy.quarters.find(q => q.period === currentQuarter);
  };

  const currentQuarterData = getCurrentQuarterData();
  const annualTarget = displayHierarchy?.annual;
  
  // For admin: calculate from quarterly targets (to get true aggregated total)
  // For regular users: use annual target or calculate from their quarterly targets
  const quarterlyTotal = displayHierarchy?.quarters && displayHierarchy.quarters.length > 0 ? {
    targetAmount: displayHierarchy.quarters.reduce((sum, q) => sum + q.targetAmount, 0),
    achievedAmount: displayHierarchy.quarters.reduce((sum, q) => sum + q.achievedAmount, 0)
  } : null;
  
  // For admin: prioritize quarterly total (aggregated from all users)
  // For regular user: use annual target first, then quarterly total
  const calculatedAnnualTarget = currentUser?.role === 'tenant_admin' 
    ? (quarterlyTotal || annualTarget || null)
    : (annualTarget || quarterlyTotal || null);
  
  console.log('Display hierarchy quarters:', displayHierarchy?.quarters);
  console.log('Quarterly total:', quarterlyTotal);
  console.log('Annual target:', annualTarget);
  console.log('User role:', currentUser?.role);
  console.log('Calculated annual target (used for display):', calculatedAnnualTarget);
  
  // Calculate overall progress
  const overallProgress = calculatedAnnualTarget && calculatedAnnualTarget.targetAmount > 0 
    ? (calculatedAnnualTarget.achievedAmount / calculatedAnnualTarget.targetAmount) * 100 
    : 0;

  // Calculate current quarter progress
  const currentQuarterProgress = currentQuarterData && currentQuarterData.targetAmount > 0
    ? (currentQuarterData.achievedAmount / currentQuarterData.targetAmount) * 100
    : 0;

  const handleRefreshData = async () => {
    await refreshAchievements(selectedYear);
    await fetchUserTargets(selectedYear);
    await fetchAllUsersTargets(selectedYear);
    await fetchDeals();
    await fetchDealStats();
  };

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <TrophyOutlined className="text-blue-500" />
            <div>
              <Title level={4} className="mb-0">Targets</Title>
              {currentUser?.role === 'tenant_admin' && (
                <Text className="text-xs text-gray-500 dark:text-gray-400">All Users Aggregated</Text>
              )}
            </div>
          </div>
          <Button
            type="text"
            icon={<SettingOutlined />}
            size="small"
            onClick={() => navigate('/admin/settings')}
            className="text-gray-500 hover:text-blue-500"
          />
        </div>
        
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          className="w-full"
          suffixIcon={<CalendarOutlined />}
        >
          {[2023, 2024, 2025, 2026].map(year => (
            <Option key={year} value={year}>{year}</Option>
          ))}
        </Select>
      </div>

      <div className="p-4 space-y-4">
        {targetLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <Text className="block mt-2 text-gray-500">Loading targets...</Text>
          </div>
        ) : targetError ? (
          <Alert
            message="Error Loading Targets"
            description={targetError}
            type="error"
            showIcon
            className="mb-4"
          />
        ) : (
          <>
            {/* Overall Achievement */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="text-center mb-3">
                <div className="mb-2">
                  <Text className="text-xs text-gray-500 dark:text-gray-400 block">Remaining Target</Text>
                  <Text className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency((calculatedAnnualTarget?.targetAmount || 0) - (calculatedAnnualTarget?.achievedAmount || 0))}
                  </Text>
                </div>
                <div className="flex justify-center items-center gap-2 text-sm">
                  <div>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Achieved</Text>
                    <Text className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(calculatedAnnualTarget?.achievedAmount || 0)}
                    </Text>
                  </div>
                  <Text className="text-gray-400">/</Text>
                  <div>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Target</Text>
                    <Text className="font-semibold">
                      {formatCurrency(calculatedAnnualTarget?.targetAmount || 0)}
                    </Text>
                  </div>
                </div>
              </div>
              <Progress
                percent={Number(overallProgress.toFixed(2))}
                strokeColor="#52c41a"
                trailColor="#e6f7ff"
                className="mb-2"
              />
              <Text className="text-sm text-center block text-gray-600 dark:text-gray-400">
                {overallProgress.toFixed(2)}% Achieved
              </Text>
            </div>

            {/* Annual Target */}
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Text className="font-medium">Annual Target {!annualTarget && displayHierarchy?.quarters?.length > 0 ? '(from Quarters)' : ''}</Text>
                <TrophyOutlined className="text-yellow-500" />
              </div>
              <Text className="text-lg font-bold">
                {formatCurrency(calculatedAnnualTarget?.targetAmount || 0)}
              </Text>
            </div>

            <Divider className="my-3" />

            {/* Current Quarter Only */}
            <div>
              <Text className="font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                Current Quarter
              </Text>
              {currentQuarterData ? (
                <div className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <CalendarOutlined className="text-blue-500" />
                      <Text className="font-medium text-blue-600 dark:text-blue-400">
                        Q{currentQuarter}
                      </Text>
                    </div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  </div>
                  <div className="mb-2">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Target</Text>
                    <Text className="text-sm font-semibold block">
                      {formatCurrency(currentQuarterData.targetAmount)}
                    </Text>
                  </div>
                  <div className="mb-2">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Remaining</Text>
                    <Text className="text-sm font-bold text-orange-600 dark:text-orange-400 block">
                      {formatCurrency(currentQuarterData.targetAmount - currentQuarterData.achievedAmount)}
                    </Text>
                  </div>
                  <Progress
                    percent={Number(currentQuarterProgress.toFixed(2))}
                    size="small"
                    strokeColor="#52c41a"
                    trailColor="#e6f7ff"
                    showInfo={false}
                  />
                  <div className="flex justify-between mt-1">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      {currentQuarterProgress.toFixed(2)}% achieved
                    </Text>
                    <Text className="text-xs text-green-600 dark:text-green-400 font-semibold">
                      {formatCurrency(currentQuarterData.achievedAmount)}
                    </Text>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                  <Text className="text-center text-gray-500 dark:text-gray-400">
                    No target set for Q{currentQuarter}
                  </Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => navigate('/admin/settings')}
                    className="w-full mt-2"
                  >
                    Set Target
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Quick Actions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <Space direction="vertical" className="w-full">
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => navigate('/deals/new')}
              className="w-full"
              size="small"
            >
              Add Deal
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshData}
              className="w-full"
              size="small"
              loading={targetLoading}
            >
              Refresh Data
            </Button>
          </Space>
        </div>

        {/* Performance Indicator */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-3 rounded-lg">
          <div className="text-center">
            <Text className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
              Current Performance
            </Text>
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                overallProgress >= 75 ? 'bg-green-500' :
                overallProgress >= 50 ? 'bg-yellow-500' :
                overallProgress >= 25 ? 'bg-orange-500' : 'bg-red-500'
              }`} />
              <Text className="text-sm font-medium">
                {overallProgress >= 75 ? 'Excellent' :
                 overallProgress >= 50 ? 'Good' :
                 overallProgress >= 25 ? 'Fair' : 'Needs Attention'}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetSidebar;
